import React from 'react';
import { Info, Target, Plus } from 'lucide-react';
import { Input, Textarea, ArrayField, KeywordTags } from './FormComponents';
import './CourseCreationTabs.css';

export interface FAQ {
  question: string;
  answer: string;
}

export interface CourseDetailsTabProps {
  tags: string[];
  requirements: string[];
  outcomes: string[];
  faqs: FAQ[];

  onAddTag: (tag: string) => void;
  onRemoveTag: (index: number) => void;
  onAddRequirement: () => void;
  onRemoveRequirement: (index: number) => void;
  onUpdateRequirement: (index: number, value: string) => void;
  onAddOutcome: () => void;
  onRemoveOutcome: (index: number) => void;
  onUpdateOutcome: (index: number, value: string) => void;
  onAddFAQ: () => void;
  onRemoveFAQ: (index: number) => void;
  onUpdateFAQ: (index: number, faq: FAQ) => void;

  validationErrors: Record<string, string>;
}

export const CourseDetailsTab: React.FC<CourseDetailsTabProps> = ({
  tags,
  requirements,
  outcomes,
  faqs,
  onAddTag,
  onRemoveTag,
  onAddRequirement,
  onRemoveRequirement,
  onUpdateRequirement,
  onAddOutcome,
  onRemoveOutcome,
  onUpdateOutcome,
  onAddFAQ,
  onRemoveFAQ,
  onUpdateFAQ,
  validationErrors,
}) => {
  return (
    <div className="tab-content">
      {/* Section Header */}
      <div className="section-header details-header">
        <h3>
          <Info size={20} />
          Course Details
        </h3>
        <p>Add keywords, requirements, learning outcomes, and FAQs</p>
      </div>

      {/* Form Grid */}
      <div className="form-grid">
        {/* Left Column */}
        <div className="form-column">
          {/* Tags / Keywords */}
          <div className="form-group">
            <label className="form-label">Tags / Keywords</label>
            <p className="form-help">Add keywords to help students find your course</p>
            <KeywordTags
              keywords={tags}
              onAdd={onAddTag}
              onRemove={onRemoveTag}
              placeholder="e.g., leadership, management"
              error={validationErrors.tags}
            />
          </div>

          {/* Learning Outcomes */}
          <ArrayField
            label="Learning Outcomes"
            items={outcomes}
            onAdd={onAddOutcome}
            onRemove={onRemoveOutcome}
            renderItem={(item, index) => (
              <Input
                icon={Target}
                value={item}
                onChange={(e) => onUpdateOutcome(index, e.target.value)}
                placeholder="What will students learn?"
              />
            )}
            addButtonText="Add Outcome"
            icon={Plus}
            className="outcomes-field"
          />
        </div>

        {/* Right Column */}
        <div className="form-column">
          {/* Requirements */}
          <ArrayField
            label="Course Requirements"
            items={requirements}
            onAdd={onAddRequirement}
            onRemove={onRemoveRequirement}
            renderItem={(item, index) => (
              <Input
                value={item}
                onChange={(e) => onUpdateRequirement(index, e.target.value)}
                placeholder="Enter a requirement"
              />
            )}
            addButtonText="Add Requirement"
            icon={Plus}
          />

          {/* FAQs */}
          <div className="faq-section">
            <label className="form-label">Frequently Asked Questions</label>
            <p className="form-help">Address common questions students might have</p>
            <div className="faq-items">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <div className="faq-header">
                    <span className="faq-title">FAQ #{index + 1}</span>
                    {faqs.length > 1 && (
                      <button
                        type="button"
                        className="remove-faq-btn"
                        onClick={() => onRemoveFAQ(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <Input
                    value={faq.question}
                    onChange={(e) =>
                      onUpdateFAQ(index, { ...faq, question: e.target.value })
                    }
                    placeholder="Question"
                  />
                  <Textarea
                    value={faq.answer}
                    onChange={(e) =>
                      onUpdateFAQ(index, { ...faq, answer: e.target.value })
                    }
                    rows={3}
                    placeholder="Answer"
                  />
                </div>
              ))}
              <button
                type="button"
                className="add-faq-btn"
                onClick={onAddFAQ}
              >
                <Plus size={16} />
                Add FAQ
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
